import { useQuery } from "@tanstack/react-query";
import { jsonFetch } from "@/lib/json-fetch";

interface Cell {
  week: number;
  role: "R1" | "R2" | "R3";
  solutionId: string;
  solutionName: string;
  account: string;
  tier: "Strong" | "Moderate" | "At Risk" | null;
  handover: boolean;
}

interface ResourceRow {
  id: string;
  name: string;
  initials: string;
  capacity: number;
  totalPoints: number;
  weekLoad: number[];
  cells: Cell[];
}

interface HeatmapData {
  weeks: number[];
  baseDate: string;
  resources: ResourceRow[];
}

function tierClass(tier: Cell["tier"]) {
  if (tier === "Strong") return "bg-[var(--fit-strong)] text-white";
  if (tier === "Moderate") return "bg-[var(--fit-moderate)] text-white";
  if (tier === "At Risk") return "bg-[var(--fit-at-risk)] text-white";
  return "bg-brand-600 text-white";
}

function handoverPattern() {
  // Diagonal hatching for forward-dated swap weeks.
  return {
    backgroundImage:
      "repeating-linear-gradient(45deg, rgba(0,0,0,0.18) 0 3px, transparent 3px 6px)",
  };
}

export function ResourceHeatmap({ onOpenResource }: { onOpenResource: (id: string) => void }) {
  const { data, isLoading } = useQuery<HeatmapData>({
    queryKey: ["dashboard-heatmap"],
    queryFn: () => jsonFetch<HeatmapData>("/api/dashboard/heatmap"),
  });

  if (isLoading || !data) {
    return (
      <div className="rounded-md border border-dashed border-border bg-card/50 px-6 py-12 text-center text-[12px] text-muted-foreground">
        Loading heatmap…
      </div>
    );
  }

  // Bucket cells per (resource, week) for fast rendering.
  const cellsByRes = new Map<string, Map<number, Cell[]>>();
  for (const r of data.resources) {
    const inner = new Map<number, Cell[]>();
    for (const c of r.cells) {
      const list = inner.get(c.week) ?? [];
      list.push(c);
      inner.set(c.week, list);
    }
    cellsByRes.set(r.id, inner);
  }

  return (
    <div className="overflow-x-auto rounded-md border border-border bg-card">
      <table className="w-full text-[11px]">
        <thead className="sticky top-0 bg-muted/40 text-left text-[10px] uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="w-44 px-3 py-2 font-semibold">Resource</th>
            <th className="w-14 px-2 py-2 text-center font-semibold">Load</th>
            {data.weeks.map((w) => (
              <th
                key={w}
                className={`px-1 py-2 text-center font-semibold ${w === 0 ? "text-foreground" : ""}`}
              >
                {w === 0 ? "Now" : w > 0 ? `+${w}` : `${w}`}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.resources.map((r) => {
            const over = r.totalPoints > r.capacity;
            return (
              <tr key={r.id} className="border-t border-border hover:bg-muted/20">
                <td className="px-3 py-1.5">
                  <button
                    type="button"
                    onClick={() => onOpenResource(r.id)}
                    className="flex items-center gap-2 text-left hover:underline"
                  >
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-foreground/85 text-[9px] font-semibold text-background">
                      {r.initials}
                    </span>
                    <span className="truncate font-medium">{r.name}</span>
                  </button>
                </td>
                <td className="px-2 py-1.5 text-center">
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                      over
                        ? "bg-[var(--fit-at-risk-bg)] text-[var(--fit-at-risk)]"
                        : r.totalPoints >= r.capacity
                          ? "bg-[var(--fit-moderate-bg)] text-[var(--fit-moderate)]"
                          : "bg-muted text-foreground"
                    }`}
                  >
                    {r.totalPoints}/{r.capacity}
                  </span>
                </td>
                {data.weeks.map((w) => {
                  const cells = cellsByRes.get(r.id)?.get(w) ?? [];
                  if (cells.length === 0) {
                    return <td key={w} className="h-7 border-l border-border/50 bg-muted/10" />;
                  }
                  // Stack multiple Solutions in the same week (rare for Primaries).
                  return (
                    <td key={w} className="border-l border-border/50 p-0">
                      <div className="flex h-7 flex-col">
                        {cells.map((c) => (
                          <div
                            key={`${c.solutionId}-${c.role}-${c.week}`}
                            title={`${c.role} on ${c.solutionName} (${c.account})${c.handover ? " · handover" : ""}`}
                            className={`flex flex-1 items-center justify-center text-[8px] font-semibold ${tierClass(c.tier)}`}
                            style={c.handover ? handoverPattern() : undefined}
                          >
                            {c.role}
                          </div>
                        ))}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
